'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Swords, Trophy, TrendingUp, TrendingDown, Clock, Crown, Zap } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { timeAgo } from '@/lib/dates';
import { useLang } from '@/lib/i18n/LanguageContext';

interface BattleTrader {
  userId: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  initial: string;
  battlesWon: number;
  battleWinRate: number;
  direction: 'BUY' | 'SELL';
  entryPrice: number;
  stopLoss: number;
  takeProfit: number;
  tradeStatus: string;
  tradeId: string;
}

interface Battle {
  id: string;
  ticker: string;
  timeframe: string;
  status: 'ACTIVE' | 'RESOLVED';
  createdAt: string;
  resolvedAt: string | null;
  winnerId: string | null;
  loserId: string | null;
  resolution?: 'WIN' | 'DRAW' | null;
  traderA: BattleTrader;
  traderB: BattleTrader;
}

interface BattleWinner {
  userId: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  initial: string;
  battlesWon: number;
  battlesLost: number;
  battleWinRate: number;
  performanceScore: number;
}

const TF_LABELS: Record<string, string> = {
  M1: '1m', M5: '5m', M15: '15m', M30: '30m', H1: '1H', H4: '4H', D1: 'D1',
};

function formatPrice(price: number): string {
  if (!price) return '—';
  if (price >= 10000) return price.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (price >= 100) return price.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 });
}

// Trader-slot card inside a battle — same status-pill/level-cell language as TradeCard:
// gold entry, red SL, green TP, border-only outcome pill.
function TraderCard({ trader, isWinner, isLoser }: { trader: BattleTrader; isWinner: boolean; isLoser: boolean }) {
  const isBuy = trader.direction === 'BUY';
  return (
    <div className="flex-1 p-4 rounded-xl relative overflow-hidden"
      style={{
        background: isWinner ? 'var(--bullish-muted)' : isLoser ? 'var(--bearish-muted)' : 'var(--bg-sunken)',
        border: isWinner ? '1px solid var(--bullish)' : isLoser ? '1px solid var(--bearish)' : '1px solid var(--border)',
      }}>
      {isWinner && (
        <div className="absolute top-2 right-2">
          <Crown size={14} style={{ color: 'var(--win)' }} />
        </div>
      )}
      {/* Avatar + name */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 overflow-hidden"
          style={{ background: trader.avatarUrl ? undefined : 'var(--accent)', color: 'var(--bg-base)' }}>
          {trader.avatarUrl ? <img src={trader.avatarUrl} alt={trader.username} className="w-full h-full object-cover" /> : trader.initial}
        </div>
        <div className="min-w-0">
          <Link href={`/profile/${trader.username}`}
            className="text-sm font-semibold truncate block hover:underline" style={{ color: 'var(--text-primary)' }}>
            @{trader.username}
          </Link>
          {trader.battlesWon > 0 && (
            <span className="text-xs" style={{ color: 'var(--win)' }}>{trader.battlesWon}W · {trader.battleWinRate.toFixed(0)}% WR</span>
          )}
        </div>
      </div>

      {/* Direction badge */}
      <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-bold mb-3 ${isBuy ? 'badge-long' : 'badge-short'}`}>
        {isBuy ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
        {isBuy ? 'LONG' : 'SHORT'}
      </div>

      {/* Levels */}
      <div className="flex flex-col gap-1.5">
        {[
          { label: 'Entry', value: trader.entryPrice, color: 'var(--win)' },
          { label: 'SL', value: trader.stopLoss, color: 'var(--bearish)' },
          { label: 'TP', value: trader.takeProfit, color: 'var(--bullish)' },
        ].map(({ label, value, color }) => (
          <div key={label} className="flex items-center justify-between">
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{label}</span>
            <span className="text-xs font-mono-nums font-semibold" style={{ color }}>{formatPrice(value)}</span>
          </div>
        ))}
      </div>

      {/* Trade status — border-only pill per TradeCard convention. OPEN shows nothing;
          PENDING (limit order not filled yet) and NOT_TRIGGERED get neutral pills. */}
      {trader.tradeStatus !== 'OPEN' && (() => {
        const st = trader.tradeStatus;
        const tone = st === 'WIN' ? 'var(--bullish)' : st === 'LOSS' ? 'var(--bearish)' : st === 'BE' ? 'var(--win)' : 'var(--text-muted)';
        const label = st === 'WIN' ? '✓ HIT TP' : st === 'LOSS' ? '✗ HIT SL' : st === 'BE' ? '~ BE'
          : st === 'PENDING' ? '⏳ PENDING' : st === 'NOT_TRIGGERED' ? '— NOT TRIGGERED' : st;
        return (
          <div className="mt-3 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
            <span className="text-xs font-extrabold px-2 py-0.5 rounded-full"
              style={{ background: 'transparent', border: `1.5px solid ${tone}`, color: tone }}>
              {label}
            </span>
          </div>
        );
      })()}
    </div>
  );
}

function BattleCard({ battle }: { battle: Battle }) {
  const { t } = useLang();
  const isResolved = battle.status === 'RESOLVED';
  const winnerIsA = battle.winnerId === battle.traderA.userId;
  const winnerIsB = battle.winnerId === battle.traderB.userId;

  return (
    <div className="card-base overflow-hidden">
      {/* Battle header */}
      <div className="flex items-center justify-between px-4 py-3 border-b flex-wrap gap-2" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2">
          <Swords size={14} style={{ color: 'var(--win)' }} />
          <span className="text-sm font-bold" style={{ color: 'var(--win)' }}>⚔ {t('Trade Battle')}</span>
          <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: 'var(--win-muted)', color: 'var(--win)' }}>
            {battle.ticker}
          </span>
          <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-sunken)', color: 'var(--text-secondary)' }}>
            {TF_LABELS[battle.timeframe] || battle.timeframe}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{
              background: 'transparent',
              border: `1.5px solid ${isResolved ? 'var(--text-dim)' : 'var(--bullish)'}`,
              color: isResolved ? 'var(--text-muted)' : 'var(--bullish)',
            }}
          >
            {isResolved ? (battle.winnerId ? t('RESOLVED') : t('DRAW')) : `● ${t('ACTIVE')}`}
          </span>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{timeAgo(battle.createdAt)}</span>
        </div>
      </div>

      {/* Battle body: A vs B */}
      <div className="p-4">
        <div className="flex items-stretch gap-3">
          <TraderCard trader={battle.traderA} isWinner={isResolved && winnerIsA} isLoser={isResolved && !winnerIsA && !!battle.winnerId} />

          {/* VS divider */}
          <div className="flex flex-col items-center justify-center gap-2 shrink-0">
            <div className="w-px flex-1" style={{ background: 'var(--border)' }} />
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ background: 'var(--win-muted)', color: 'var(--win)', border: '1px solid rgba(176,124,31,0.3)' }}>
              VS
            </div>
            <div className="w-px flex-1" style={{ background: 'var(--border)' }} />
          </div>

          <TraderCard trader={battle.traderB} isWinner={isResolved && winnerIsB} isLoser={isResolved && !winnerIsB && !!battle.winnerId} />
        </div>

        {/* Result banner */}
        {isResolved && battle.winnerId && (
          <div className="mt-3 p-3 rounded-xl flex items-center justify-center gap-2"
            style={{ background: 'var(--bullish-muted)', border: '1px solid var(--bullish)' }}>
            <Crown size={14} style={{ color: 'var(--win)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--bullish)' }}>
              @{winnerIsA ? battle.traderA.username : battle.traderB.username} {t('wins this battle!')}
            </span>
          </div>
        )}
        {isResolved && !battle.winnerId && (
          <div className="mt-3 p-3 rounded-xl flex items-center justify-center gap-2"
            style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
            <Swords size={14} style={{ color: 'var(--text-muted)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
              {t('Draw — neither trade hit Take Profit')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BattlesPage() {
  const { t } = useLang();
  const [activeTab, setActiveTab] = useState<'active' | 'recent' | 'winners'>('active');
  const [battles, setBattles] = useState<Battle[]>([]);
  const [winners, setWinners] = useState<BattleWinner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    if (activeTab === 'winners') {
      fetch('/api/battles?section=winners')
        .then((r) => r.json())
        .then((d) => { setWinners(Array.isArray(d.winners) ? d.winners : []); })
        .catch(() => setWinners([]))
        .finally(() => setLoading(false));
    } else {
      fetch(`/api/battles?section=${activeTab === 'active' ? 'active' : 'recent'}`)
        .then((r) => r.json())
        .then((d) => { setBattles(Array.isArray(d.battles) ? d.battles : []); })
        .catch(() => setBattles([]))
        .finally(() => setLoading(false));
    }
  }, [activeTab]);

  const tabs = [
    { id: 'active', label: 'Active Battles', icon: <Zap size={14} /> },
    { id: 'recent', label: 'Recent Battles', icon: <Clock size={14} /> },
    { id: 'winners', label: 'Top Battle Winners', icon: <Trophy size={14} /> },
  ] as const;

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-base)' }}>
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 py-8 pb-28 md:pb-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'var(--win-muted)' }}>
            <Swords size={20} style={{ color: 'var(--win)' }} />
          </div>
          <div>
            <h1 className="text-xl font-bold font-heading" style={{ color: 'var(--text-primary)' }}>{t('Trade Battles')}</h1>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('Trader vs Trader — who called it right?')}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium shrink-0 transition-all duration-150"
              style={{
                background: activeTab === tab.id ? 'var(--win-muted)' : 'var(--bg-sunken)',
                color: activeTab === tab.id ? 'var(--win)' : 'var(--text-secondary)',
                border: activeTab === tab.id ? '1px solid rgba(176,124,31,0.3)' : '1px solid var(--border)',
              }}
            >
              <span style={{ color: activeTab === tab.id ? 'var(--win)' : 'var(--text-muted)' }}>{tab.icon}</span>
              {t(tab.label)}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-[20px] skeleton" style={{ height: 220 }} />
            ))}
          </div>
        ) : activeTab === 'winners' ? (
          <div className="card-base overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
              <Trophy size={14} style={{ color: 'var(--win)' }} />
              <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t('Top Battle Traders')}</span>
            </div>
            {winners.length === 0 ? (
              <div className="py-16 text-center">
                <Trophy size={36} className="mx-auto mb-3" style={{ color: 'var(--text-dim)' }} />
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('No battle winners yet')}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{t('Post opposing trade ideas to start battles')}</p>
              </div>
            ) : (
              <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
                {winners.map((w, idx) => (
                  <div key={w.userId} className="flex items-center gap-4 px-4 py-3.5 transition-colors duration-100"
                    onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.background = 'var(--bg-card-hover)')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.background = 'transparent')}>
                    <span className="text-sm font-bold w-6 text-center shrink-0" style={{ color: idx < 3 ? ['var(--win)', 'var(--text-secondary)', '#b06a3a'][idx] : 'var(--text-muted)' }}>
                      {idx < 3 ? ['🥇', '🥈', '🥉'][idx] : idx + 1}
                    </span>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 overflow-hidden"
                      style={{ background: w.avatarUrl ? undefined : 'var(--accent)', color: 'var(--bg-base)' }}>
                      {w.avatarUrl ? <img src={w.avatarUrl} alt={w.username} className="w-full h-full object-cover" /> : w.initial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link href={`/profile/${w.username}`}
                        className="text-sm font-semibold hover:underline" style={{ color: 'var(--text-primary)' }}>
                        @{w.username}
                      </Link>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs" style={{ color: 'var(--bullish)' }}>{w.battlesWon}W</span>
                        <span className="text-xs" style={{ color: 'var(--bearish)' }}>{w.battlesLost}L</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-bold font-mono-nums" style={{ color: w.battleWinRate >= 60 ? 'var(--bullish)' : w.battleWinRate >= 40 ? 'var(--win)' : 'var(--bearish)' }}>
                        {w.battleWinRate.toFixed(0)}%
                      </div>
                      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('Win Rate')}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-bold font-mono-nums" style={{ color: 'var(--win)' }}>{w.battlesWon}</div>
                      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('Battles Won')}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : battles.length === 0 ? (
          <div className="py-20 text-center">
            <Swords size={48} className="mx-auto mb-4" style={{ color: 'var(--text-dim)' }} />
            <p className="text-base font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
              {activeTab === 'active' ? t('No active battles right now') : t('No recent battles')}
            </p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {t('Battles are created automatically when two traders post opposing ideas on the same instrument')}
            </p>
            <Link href="/home-feed" className="inline-flex items-center gap-2 mt-4 btn-primary px-4 py-2 text-sm">
              <TrendingUp size={14} />
              {t('Post a Trade Idea')}
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {battles.map((battle) => (
              <BattleCard key={battle.id} battle={battle} />
            ))}
          </div>
        )}
      </main>

    </div>
  );
}
